/**
 * `AdBreakpoint` — declarative marker for a **breathing point**.
 *
 * It is mounted ONLY on "done"/summary screens (never in an editor, a capture
 * flow, or a list with active scrolling). When `trigger` becomes `true`
 * —that is, when the value unit COMPLETED—it checks the gate:
 *
 *   - `interstitial`: if the gate allows it AND there is fill, it opens the
 *     full-screen overlay and records the impression in local capping. **Immediately closable**
 *     (button + "back"), never with a countdown.
 *   - `native`: delegates to `NativeAdCard` (reserved space, CLS=0).
 *
 * If the gate blocks or there is no fill, it **renders nothing** and leaves no gap: one
 * ad is better than one extra ad.
 */
import { msg } from "@lingui/core/macro";
import { createEffect, createSignal, Show } from "solid-js";
import { X } from "@fleet/icons";
import { LISTTO_AD_PLACEMENTS, type ListtoPlacementId, placementDef } from "@listto/shared";
import { Button } from "@/components/ui/button";
import { useBackDismiss } from "@/lib/use-back-dismiss";
import { useI18n } from "@/providers/i18n-provider";
import { useAds } from "../ads-context";
import { NativeAdCard } from "./native-ad-card";

interface AdBreakpointProps {
  placement: ListtoPlacementId;
  /** Signal: the value unit completed (list closed, plan finished…). */
  trigger: boolean;
  /** Reserved height of the native slot. */
  minHeight?: string;
}

export function AdBreakpoint(props: AdBreakpointProps) {
  const { t } = useI18n();
  const ads = useAds();
  const [open, setOpen] = createSignal(false);
  const def = () => placementDef(LISTTO_AD_PLACEMENTS, props.placement);

  // "Back" closes the overlay instead of navigating (repo overlay convention).
  useBackDismiss(open, () => setOpen(false));

  // The interstitial is imperative: it fires ONCE when the value unit
  // completes. `showInterstitial` re-queries the gate and records the impression.
  createEffect((alreadyFired: boolean | undefined) => {
    if (!props.trigger || alreadyFired || def()?.kind !== "interstitial") {
      return alreadyFired ?? false;
    }
    ads?.completeValueUnit();
    void ads?.showInterstitial(props.placement).then((shown) => {
      setOpen(shown);
      return shown;
    });
    return true;
  });

  return (
    <>
      <Show when={def()?.kind === "native" && props.trigger}>
        <NativeAdCard placement={props.placement} minHeight={props.minHeight} />
      </Show>

      <Show when={open()}>
        <div
          class="fixed inset-0 z-50 flex flex-col bg-background/95 backdrop-blur-sm"
          role="dialog"
          aria-modal="true"
          aria-label={t(msg`Advertisement`)}
          data-testid={`interstitial-ad-${props.placement}`}
        >
          <div class="flex justify-end p-3">
            {/* Cerrable AL INSTANTE: sin cuenta atrás, sin "X" diminuta. */}
            <Button
              variant="ghost"
              size="icon"
              aria-label={t(msg`Close ad`)}
              data-testid="interstitial-ad-close"
              onClick={() => setOpen(false)}
            >
              <X class="size-5" aria-hidden="true" />
            </Button>
          </div>
          {/* El creativo lo pinta el SDK dentro de este contenedor. */}
          <div class="flex-1" data-ad-slot={props.placement} />
        </div>
      </Show>
    </>
  );
}
